import React, { useEffect, useState } from 'react';
import { Sparkles, BriefcaseBusiness, ClipboardCheck } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { appDesignTokens as tokens } from './appDesignTokens';
import { useTheme } from '../context/ThemeContext';

export const APP_SIDEBAR_WIDTH = 76;

const getViewportWidth = () => {
  if (typeof window === 'undefined') return 1024;
  return Math.min(
    window.innerWidth || 1024,
    window.visualViewport?.width || window.innerWidth || 1024
  );
};

export default function AppSidebar({ width = APP_SIDEBAR_WIDTH, topOffset = 0, fixed = false }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { isDark } = useTheme();
  const [isMobileLayout, setIsMobileLayout] = useState(() => getViewportWidth() <= 720);

  useEffect(() => {
    const update = () => setIsMobileLayout(getViewportWidth() <= 720);
    update();
    window.addEventListener('resize', update);
    window.visualViewport?.addEventListener('resize', update);
    return () => {
      window.removeEventListener('resize', update);
      window.visualViewport?.removeEventListener('resize', update);
    };
  }, []);

  const menuItems = [
    { id: 'insights', icon: Sparkles, label: 'Insights', path: '/insights', matchPrefixes: ['/insights', '/insights-list', '/dashboard', '/company-details'] },
    { id: 'jobs', icon: BriefcaseBusiness, label: 'Jobs', path: '/jobs', matchPrefixes: ['/jobs', '/mailer-jobs'] },
    { id: 'tracker', icon: ClipboardCheck, label: 'Tracker', path: '/applications', matchPrefixes: ['/applications'] },
  ];

  return (
    <aside
      className="app-sidebar flex flex-col h-full"
      style={{
        '--app-sidebar-width': `${width}px`,
        width: isMobileLayout ? '100vw' : 'var(--app-sidebar-width)',
        minWidth: isMobileLayout ? '100vw' : (fixed ? undefined : 'var(--app-sidebar-width)'),
        height: isMobileLayout ? 'calc(74px + env(safe-area-inset-bottom))' : `calc(100vh - ${topOffset}px)`,
        position: isMobileLayout ? 'fixed' : (fixed ? 'fixed' : 'sticky'),
        top: isMobileLayout ? 'auto' : topOffset,
        left: isMobileLayout ? 0 : (fixed ? 0 : undefined),
        right: isMobileLayout ? 0 : undefined,
        bottom: isMobileLayout ? 0 : undefined,
        zIndex: isMobileLayout ? 45 : (fixed ? 25 : undefined),
        background: isDark ? 'rgba(255,255,255,0.03)' : tokens.colors.appShell,
        borderRight: isMobileLayout ? 'none' : `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : tokens.colors.border}`,
        borderTop: isMobileLayout ? `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : tokens.colors.border}` : undefined,
        boxShadow: isMobileLayout ? (isDark ? '0 -10px 30px rgba(0,0,0,0.5)' : '0 -10px 30px rgba(15, 23, 42, 0.06)') : undefined,
        fontFamily: tokens.fonts.body,
      }}
    >
      <nav
        className="flex-1 flex flex-col items-center justify-start py-6 gap-6"
        style={isMobileLayout ? {
          width: '100%',
          height: '100%',
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'space-around',
          gap: 0,
          padding: '8px 18px calc(8px + env(safe-area-inset-bottom))',
        } : undefined}
      >
        {menuItems.map((item) => {
          const isActive = (item.matchPrefixes || [item.path]).some((prefix) => location.pathname.startsWith(prefix));
          return (
            <button
              key={item.id}
              onClick={() => item.path !== '#' && navigate(item.path)}
              className="app-sidebar-item group flex flex-col items-center gap-1 w-full px-2 transition-colors"
              style={{
                color: isActive ? (isDark ? '#F59E0B' : tokens.colors.accent) : (isDark ? 'rgba(255,255,255,0.45)' : tokens.colors.textDim),
                minHeight: isMobileLayout ? 54 : 58,
                width: isMobileLayout ? '33.333%' : undefined,
                justifyContent: isMobileLayout ? 'center' : undefined,
                paddingLeft: isMobileLayout ? 0 : undefined,
                paddingRight: isMobileLayout ? 0 : undefined,
              }}
            >
              <div
                className="transition-all"
                style={{
                  width: 30,
                  height: 30,
                  borderRadius: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: 'transparent',
                  border: 'none',
                  boxShadow: 'none',
                }}
              >
                <item.icon className="h-5 w-5" />
              </div>
              <span className="app-sidebar-label text-[10px] font-bold uppercase tracking-wider" style={{ lineHeight: 1.1 }}>{item.label}</span>
            </button>
          );
        })}
      </nav>
      <style>{`
        @media (max-width: 720px), (max-device-width: 720px) {
          .app-sidebar {
            --app-sidebar-width: 100vw !important;
            position: fixed !important;
            top: auto !important;
            left: 0 !important;
            right: 0 !important;
            bottom: 0 !important;
            width: 100vw !important;
            min-width: 100vw !important;
            height: calc(74px + env(safe-area-inset-bottom)) !important;
            z-index: 45 !important;
            border-right: none !important;
            border-top: 1px solid ${isDark ? 'rgba(255,255,255,0.06)' : tokens.colors.border} !important;
            box-shadow: ${isDark ? '0 -10px 30px rgba(0,0,0,0.5)' : '0 -10px 30px rgba(15, 23, 42, 0.06)'};
          }
          .app-sidebar nav {
            width: 100% !important;
            height: 100% !important;
            flex-direction: row !important;
            align-items: center !important;
            justify-content: space-around !important;
            gap: 0 !important;
            padding: 8px 18px calc(8px + env(safe-area-inset-bottom)) !important;
          }
          .app-sidebar-item {
            width: 33.333% !important;
            min-height: 54px !important;
            justify-content: center !important;
            padding-left: 0 !important;
            padding-right: 0 !important;
          }
          .app-sidebar-label {
            font-size: 9px !important;
          }
        }
      `}</style>
    </aside>
  );
}
